import { Box, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../contexts/ConfigContext";
import { isCmTableEndpoint } from "../../utilities/cmTableEndpoint";

export default function QuickMenu() {
  const config = useConfig();
  const navigate = useNavigate();

  // Static shortcuts from config (Upload, Media, legacy endpoints, etc.)
  const configShortcuts = config.homePageShortcuts ?? [];

  // CM Table endpoints derived from config
  const cmTableEndpoints = (config.apiEndpoints ?? []).filter(isCmTableEndpoint);

  const handleCardClick = (tableName: string) => {
    const allEndpoints = config.apiEndpoints ?? [];
    const exists = allEndpoints.some((ep) => ep.tableName === tableName);
    const target = exists ? tableName : allEndpoints[0]?.tableName;
    if (target) navigate(`/map?endpoint=${target}`);
  };

  // Tab index -> route mapping (mirrors the old AppContent tab ordering)
  const handleShortcutClick = (card: (typeof configShortcuts)[number]) => {
    if (card.path) {
      navigate(card.path);
      return;
    }
    switch (card.targetTab) {
      case 3:
        navigate("/upload");
        break;
      case 4:
        navigate("/editor");
        break;
      case 5:
        navigate("/tools");
        break;
      default:
        // targetTab 1 (Map) or 2 (Tables) — navigate with endpoint
        if (card.options.tableName) handleCardClick(card.options.tableName);
    }
  };

  return (
    <Card
      sx={{
        width: {
          xs: "90%",
          md: "80dvw"
        },
        maxWidth: "80rem",
        minHeight: "240px", // 80% of 300px
        maxHeight: "72dvh", // 80% of 90vh
        backgroundColor: "grey.800",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        borderRadius: "0 0 0.5rem 0.5rem",
        borderTop: "6px solid",
        borderTopColor: "primary.main",
        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        overflow: "auto",
        "&:hover": {
          boxShadow: "0 12px 48px rgba(0, 0, 0, 0.4)"
        }
      }}
      className='scroller'
    >
      <CardContent sx={{ p: 0 }}>
        <Grid container spacing={3} alignItems='stretch'>
          <Grid item xs={12} md={5}>
            <Stack
              gap={"1.75rem"}
              sx={{
                padding: {
                  xs: "2rem 1rem",
                  lg: "3.5rem 1.75rem 3.5rem 3.5rem"
                },
                flexDirection: {
                  sm: "row",
                  md: "column"
                }
              }}
            >
              <Box
                sx={{
                  height: "17.5rem",
                  width: "100%",
                  minWidth: { xs: "14rem", sm: "20rem", md: "auto" },
                  maxWidth: {
                    xs: "100%",
                    sm: "20rem",
                    md: "100%"
                  }
                }}
              >
                <img
                  src={config.loginImagePath ?? "icons/lock.png"}
                  alt='login'
                  style={{
                    objectFit: "contain",
                    objectPosition: "center",
                    width: "100%",
                    height: "100%",
                    borderRadius: "0.5rem",
                    aspectRatio: "1/1"
                  }}
                />
              </Box>
              <Typography
                variant='body1'
                align='justify'
                color='grey.100'
                sx={{
                  px: 1.6, // 80% of 2
                  lineHeight: "normal"
                }}
              >
                {config.homePageDescription ?? "Welcome to Culture Map Demo..."}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack
              sx={{
                gap: "2.5rem",
                padding: {
                  xs: "2rem 1rem",
                  lg: "3.5rem 3rem 3.5rem 1.75rem"
                }
              }}
            >
              <Box sx={{ paddingRight: "0.5rem" }}>
                <Typography variant='h2' fontWeight={600} color='grey.25'>
                  Choose how you'd like to explore the database
                </Typography>
              </Box>
              <Grid
                container
                rowSpacing={"2rem"}
                columnSpacing={"1.5rem"}
                className='scroller'
                sx={{
                  maxHeight: "30rem",
                  paddingRight: "0.5rem",
                  overflowY: "auto"
                }}
              >
                {/* Static config shortcuts (Upload, Media, legacy endpoints etc.) */}
                {configShortcuts.map((card, index) => (
                  <Grid item xs={6} sm={4} md={6} lg={4} key={`config-${index}`}>
                    <Card
                      onClick={() => handleShortcutClick(card)}
                      elevation={0}
                      sx={{
                        display: "flex",
                        bgcolor: "grey.700",
                        height: { xs: 95, sm: "8em" },
                        width: "100%",
                        padding: "2.375rem 0.275rem",
                        border: "1px solid",
                        borderColor: "grey.600",
                        borderRadius: "12px",
                        cursor: "pointer",
                        justifyContent: "center",
                        alignItems: "center",
                        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                        "&:hover": {
                          color: "primary.main",
                          transform: "translateY(-0.125rem) scale(1.03)",
                          border: "1px solid",
                          borderColor: "primary.main",
                          backgroundColor: "grey.800"
                        },
                        "&:active": {
                          transform: "translateY(-3px) scale(1.03)",
                          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                          transition: "all 0.1s ease-out"
                        }
                      }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          padding: "16px !important"
                        }}
                      >
                        <Typography
                          variant='h4'
                          align='center'
                          sx={{
                            color: "grey.25",
                            wordBreak: "break-word",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            fontSize: { xs: "0.76rem", sm: "0.84rem", md: "0.92rem" },
                            fontWeight: 500,
                            letterSpacing: "0.01em",
                            lineHeight: 1.2
                          }}
                        >
                          {card.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {cmTableEndpoints.map((ep) => (
                  <Grid item xs={6} sm={4} md={6} lg={4} key={ep.tableName}>
                    <Card
                      onClick={() => handleCardClick(ep.tableName)}
                      elevation={0}
                      sx={{
                        display: "flex",
                        bgcolor: "grey.700",
                        height: { xs: 95, sm: "8em" },
                        width: "100%",
                        padding: "2.375rem 0.275rem",
                        border: "1px solid",
                        borderColor: "grey.600",
                        borderRadius: "12px",
                        cursor: "pointer",
                        justifyContent: "center",
                        alignItems: "center",
                        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                        "&:hover": {
                          color: "primary.main",
                          transform: "translateY(-0.125rem) scale(1.03)",
                          border: "1px solid",
                          borderColor: "primary.main",
                          backgroundColor: "grey.800"
                        },
                        "&:active": {
                          transform: "translateY(-3px) scale(1.03)",
                          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                          transition: "all 0.1s ease-out"
                        }
                      }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          padding: "16px !important"
                        }}
                      >
                        <Typography
                          variant='h4'
                          align='center'
                          sx={{
                            color: "grey.25",
                            wordBreak: "break-word",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            fontSize: { xs: "0.76rem", sm: "0.84rem", md: "0.92rem" },
                            fontWeight: 500,
                            letterSpacing: "0.01em",
                            lineHeight: 1.2
                          }}
                        >
                          {ep.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {/* Empty state — only shown if both lists are empty */}
                {configShortcuts.length === 0 && cmTableEndpoints.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant='body2' color='grey.400' sx={{ px: 1 }}>
                      No tables available for your organisation.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
